import * as React from 'react';
import {
  MouseEvent,
  RefObject,
  ReactElement,
  ReactNode, useCallback,
  useContext,
  useEffect,
  createRef,
  useState,
} from 'react';
import { Layout } from '../Layout';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';
import { PubListConfigInternal } from './types';
import { LabelledPersonSuggester } from '../components/form/LabelledPersonSuggester';
import {
  OrganisationApi,
  OrganisationDTO,
  PersonApi,
  PersonDTO,
  ProjectApi,
  ProjectDTO,
  PublistConfigApi,
  PublistListApi,
  ReferencedOrganisationDTO,
  ReferencedPersonDTO,
  ReferencedWorkDTO,
  WorkApi,
  WorkDTO,
  ApiResponse,
  ConfigResponse,
  DatePeriod,
  PubListConfig,
  PubListConfigListTypeEnum,
  PublistConfigProjectOrganisationFilterMembersEnum,
  PubListConfigReplacements,
  ResponseError,
  TextApiResponse,
  YearPeriod,
} from '../../api/core';
import { AuthContext } from '../../Auth/components/AuthProvider';
import { getAuthMiddleware } from '../../Auth/utils/GetAuthMiddleware';
import { getDefaultPublist, getDefaultPubListConfigReplacements } from '../../Settings/emptyModels';
import { LabelledOrganisationSuggester } from '../components/form/LabelledOrganisationSuggester';
import { LabelledProjectSuggester } from '../components/form/LabelledProjectSuggester';
import { getVocabulary, getVocabularyOptions } from '../../Vocabulary/util';
import {
  referencedOrganisationToSelectedOrganisation,
  SelectedOrganisation,
  selectedOrganisationToReferencedOrganisation,
} from '../edit/components/suggester/OrganisationSuggester';
import {
  referencedPersonToSelectedPerson,
  SelectedPerson,
  selectedPersonToReferencedPerson,
} from '../edit/components/suggester/PersonSuggester';
import { referencedProjectToSelectedProject, SelectedProject } from '../edit/components/suggester/ProjectSuggester';
import { ExternalLink } from '../components/ExternalLink';
import { TitledAutoComplete } from './components/TitledAutoComplete';
import { SelectItem } from 'primereact/selectitem';
import { TitledCheckbox } from '../components/form/TitledCheckbox';
import { CheckboxChangeEvent } from 'primereact/checkbox';
import { publistSortOptions } from '../../Settings/data';
import { LabelledMultiSelect } from '../components/form/LabelledMultiSelect';
import { FilterByYear } from './components/FilterByYear';
import { FilterByCreated } from './components/FilterByCreated';
import { TitledYesNo } from '../components/form/TitledYesNo';
import { LabelledWorkSuggester } from '../components/form/LabelledWorkSuggester';
import { RadioButtonChangeEvent } from 'primereact/radiobutton';
import { Label } from '../components/Label';
import { Actions, MultiInputControls } from '../components/form/MultiInput/MultiInputControls';
import { InputGroup } from '../edit/components/layout/InputGroup';
import { LabelledInputText } from '../components/form/LabelledInputText';
import { Button } from 'primereact/button';
import { ToastContext, ToastContextType } from '../../util/providers/ToastProvider';
import { Blocker, NavigateFunction, Params, useBlocker, useLocation, useNavigate, useParams } from 'react-router';
import { Loading } from '../system/Loading';
import { TitledRadio } from '../components/form/TitledRadio';
import { htmlToText } from 'html-to-text';
import { Fieldset } from '../edit/components/layout/Fieldset';
import { Menu } from 'primereact/menu';
import { initialCslLocale } from '../../Settings/config';
import { ErrorComponent } from '../components/Error';
import { LabelledDropdown } from '../components/form/LabelledDropdown';
import { mapPersonNameDtosToReferencedPersonNameDtos } from '../../util/mappers/PersonNameDtoToReferencedPersonNameDto';

export const PublicationListEditor = (): ReactNode => {
  const menuDownload: RefObject<Menu> = createRef<Menu>();
  const location = useLocation();
  const navigate: NavigateFunction = useNavigate();
  const intl: IntlShape = useIntl();
  const params: Readonly<Params<string>> = useParams();
  const toast: ToastContextType = useContext(ToastContext);
  const {actions, authorizer} = useContext(AuthContext);
  const configServiceApi: PublistConfigApi = new PublistConfigApi().withMiddleware(getAuthMiddleware(actions));
  const publistServiceApi: PublistListApi = new PublistListApi().withMiddleware(getAuthMiddleware(actions));
  const personApi: PersonApi = new PersonApi().withMiddleware(getAuthMiddleware(actions));
  const workApi: WorkApi = new WorkApi().withMiddleware(getAuthMiddleware(actions));
  const organisationApi: OrganisationApi = new OrganisationApi().withMiddleware(getAuthMiddleware(actions));
  const projectApi: ProjectApi = new ProjectApi().withMiddleware(getAuthMiddleware(actions));
  const [config, setConfig] = React.useState<PubListConfigInternal>(undefined);
  const [apiKey, setApiKey] = React.useState<string>(undefined);
  const [preview, setPreview] = React.useState<string>(null);
  const [sidebarVisible, setSidebarVisible] = useState<boolean>(true);
  const [error, setError] = useState<number | undefined>(undefined);
  const [loadingPreview, setLoadingPreview] = React.useState(false);

  const [blockRequested, setBlockRequested] = React.useState<boolean>(false);
  const blocker: Blocker = useBlocker(useCallback((): boolean => blockRequested, [blockRequested]));

  const loadConfig = (apikey: string): void => {
    void configServiceApi.getConfigByApiKey({ apiKey: apikey })
      .then((cf: ConfigResponse): void => {
        setBlockRequested(false);
        void augmentConfig(cf.config)
          .then((configInternal: PubListConfigInternal): void => setConfig(configInternal));
      })
      .catch((err: unknown): void => {
        if (err instanceof ResponseError && err.response.status === 404) {
          toast.alert({
            header: intl.formatMessage({id: 'publig.message.load.notAvailable.summary'}),
            message: intl.formatMessage({id: 'publig.message.load.notAvailable.content'}),
          });
        } else {
          toast.alert({
            header: intl.formatMessage({id: 'publig.message.load.error.summary'}),
            message: intl.formatMessage({id: 'publig.message.load.error.content'}),
          });
        }
        void navigate(`/publist/generator/${params.type}`, {replace: false});
      });
  };

  const setDefaultConfig = (): void => {
    setBlockRequested(false);
    let listType: PubListConfigListTypeEnum;
    switch (params.type) {
      case 'research':
        listType = PubListConfigListTypeEnum.ResearchData;
        break;
      case 'publications':
      default:
        listType = PubListConfigListTypeEnum.Publication;
        break;
    }
    setConfig({ ...getDefaultPublist(), listType });
  };

  const parsePublistConfig = (pubListConfigInternal: PubListConfigInternal): PubListConfig => {
    const pubListConfig: PubListConfig = {};
    Object.entries(pubListConfigInternal).forEach(([key, value]: [string, any]): void => {
      if (value instanceof Array && value.length === 0) return;
      if (value === null) return;
      if (key === 'filterByYear' && (!value || (!value[0]?.start && !value[0]?.end))) return;
      switch (key) {
        case 'personIds':
          pubListConfig.personIds = (value as PersonDTO[]).map((person: PersonDTO): number => person.id);
          break;
        case 'filterPersons':
          pubListConfig.filterPersons = (value as SelectedPerson[]).map((person: SelectedPerson): number => person.id);
          break;
        case 'projectOrganisationConfig':
          pubListConfig.projectOrganisationConfig = {
            ...value,
            organisationIds: ((value.organisationIds as OrganisationDTO[])||[]).map((organisation: OrganisationDTO): number => organisation.id),
            projectIds: ((value.projectIds as ProjectDTO[])||[]).map((project: ProjectDTO): number => project.id),
          };
          break;
        case 'filterIds':
          pubListConfig.filterIds = ((value as ReferencedWorkDTO[])||[]).map((id: ReferencedWorkDTO): number => id.id);
          break;
        case 'keyIds':
          pubListConfig.keyIds = ((value as ReferencedWorkDTO[])||[]).map((id: ReferencedWorkDTO): number => id.id);
          break;
        case 'filterByCreationDate':
          pubListConfig.filterByCreationDate = (value as DatePeriod[]);
          break;
        default:
          pubListConfig[key] = value;
          break;
      }
    });
    return pubListConfig;
  };

  const loadPreview = async (event?: MouseEvent): Promise<void> => {
    if (event) event.preventDefault();
    setLoadingPreview((): boolean => true);
    try {
      setSidebarVisible(false);
      const pubListConfig = parsePublistConfig(config);
      const response: TextApiResponse = await publistServiceApi.previewRaw({pubListConfig});
      switch (true) {
        case response.raw.status >= 200 && response.raw.status < 300:
          const content: string = await response.value();
          if (content.trim().toLowerCase() === '<h1>empty list</h1>') {
            setPreview((): string => `<h1 className='empty-list'>${intl.formatMessage({id: 'publig.preview.publistNoResult'})}</h1>`);
          } else {
            setPreview((): string => content);
          }
          break;
        default:
          setError(response.raw.status);
          toast.error(response, 'message.loading.failed');
          break;
      }

    } catch (e) {
      toast.error(e, 'message.loading.failed');
    } finally {
      setLoadingPreview((): boolean => false);
    }
  };

  const _reset = (): void => {
    setPreview((): string => null);
    if (apiKey) {
      loadConfig(apiKey);
    } else {
      setDefaultConfig();
    }
  };

  useEffect((): void => {
    if (blocker.state === 'blocked') {
      if(!blockRequested) return blocker.proceed();
      toast.confirm({
        header: intl.formatMessage({id: 'popup.unsavedInput.pageChange.headline'}),
        message: intl.formatMessage({id: 'popup.unsavedInput.pageChange.message'}),
        icon: 'pi pi-info-circle',
        defaultFocus: 'reject',
        acceptClassName: 'p-button-danger',
        acceptLabel: intl.formatMessage({id: 'button.confirm'}),
        accept: (): void => {
          _reset();
          blocker.proceed();
        },
        rejectLabel: intl.formatMessage({id: 'button.abort'}),
        reject: (): void => blocker.reset(),
      });
    }
  }, [blocker.state]);

  useEffect((): void => _reset(), [apiKey]);

  useEffect((): void => setApiKey(params.apikey), [location.pathname]);

  const checkValidation = (): boolean => {
    if (!config.name || config.name.trim().length === 0) {
      return false;
    }
    if (config.personIds.length === 0 && config.projectOrganisationConfig.projectIds.length === 0 && config.projectOrganisationConfig.organisationIds.length === 0) {
      return false;
    }
    if (config.replacements && config.replacements.some((replacement: PubListConfigReplacements): boolean => !replacement.key || replacement.key.length === 0)) {
      return false;
    }
    return true;
  };

  const editConfig = (_config: PubListConfigInternal | ((pubListConfigInternal: PubListConfigInternal) => PubListConfigInternal)): void => {
    setBlockRequested(true);
    setConfig(_config);
  };

  const download = (type: 'txt' | 'html'): void => {
    const element = document.createElement('a');
    let file: Blob;
    switch (type) {
      case 'txt':
        file = new Blob([htmlToText(preview)], {type: 'text/plain'});
        break;
      case 'html':
        file = new Blob([preview], {type: 'text/html'});
        break;
      default:
        return;
    }

    element.href = URL.createObjectURL(file);
    element.download = `${config.name && config.name.length > 0 ? config.name : intl.formatMessage({id: 'publist.unnamedPublist'})}.${type}`;
    document.body.appendChild(element); // Required for this to work in FireFox
    element.click();
  };

  // PubListGenerator expects an augmented config
  const augmentConfig = async (_config: PubListConfig): Promise<PubListConfigInternal> => {
    return {
      listType: _config.listType,
      projectOrganisationConfig: {
        withOrganisationHierarchy: _config.projectOrganisationConfig?.withOrganisationHierarchy || false,
        determinationType: _config.projectOrganisationConfig?.determinationType,
        filterMembers: _config.projectOrganisationConfig?.filterMembers,
        considerEmploymentPeriod: _config.projectOrganisationConfig?.considerEmploymentPeriod || false,
        organisationIds: await Promise.all(
          (_config.projectOrganisationConfig?.organisationIds || []).map(
            (organisationId: number): Promise<ProjectDTO> =>
              organisationApi.getReducedOrganisation({
                organisationId,
                accept: 'application/orga.reduced+json',
              })
          )
        ),
        projectIds: await Promise.all(
          (_config.projectOrganisationConfig?.projectIds || []).map(
            (projectId: number): Promise<ProjectDTO> =>
              projectApi.getProjectReduced({
                projectId,
                accept: 'application/project.reduced+json',
              })
          )
        ),
        restrictToRoles: _config.projectOrganisationConfig?.restrictToRoles || [],
        excludeByRoles: _config.projectOrganisationConfig?.excludeByRoles || [],
      },
      filterByYear: _config.filterByYear || [],
      filterByCreationDate: _config.filterByCreationDate || [],
      filterByType: _config.filterByType || [],
      excludeByType: _config.excludeByType || [],
      filterByPr: _config.filterByPr || null,
      filterByRole: _config.filterByRole || [],
      filterByRoleExcl: _config.filterByRoleExcl || [],
      filterByLang: _config.filterByLang || [],
      filterPersonByRole: _config.filterPersonByRole || [],
      groupByYear: _config.groupByYear || false,
      groupByType: _config.groupByType || false,
      pubsort: _config.pubsort || 'stm',
      toc: _config.toc || false,
      locale: _config.locale || initialCslLocale,
      replacements: _config.replacements || [],
      personIds: await Promise.all(
        (_config.personIds || []).map(
          (id: number): Promise<PersonDTO> =>
            personApi
              .getPerson({ personId: id })
              .catch(
                (): PersonDTO => ({ id, personNames: [{ surname: `Could not load person ${id}` }], externalIds: [] })
              )
        )
      ).then((persons: PersonDTO[]): ReferencedPersonDTO[] =>
        persons.map(
          (person: PersonDTO): ReferencedPersonDTO => ({
            id: person.id,
            personNames: mapPersonNameDtosToReferencedPersonNameDtos(person.personNames),
            externalIds: person.externalIds,
          })
        )
      ),
      filterIds: await Promise.all(
        (_config.filterIds || []).map(
          (id: number): Promise<ReferencedWorkDTO> =>
            workApi.getWork({ workId: id }).catch(() => ({ id, title: `Could not load work ${id}` }))
        )
      ),
      filterPersons: await Promise.all(
        (_config.filterPersons || []).map(
          (id: number): Promise<PersonDTO> =>
            personApi
              .getPerson({ personId: id })
              .catch(
                (): PersonDTO => ({ id, personNames: [{ surname: `Could not load person ${id}` }], externalIds: [] })
              )
        )
      ).then((persons: PersonDTO[]): ReferencedPersonDTO[] =>
        persons.map(
          (person: PersonDTO): ReferencedPersonDTO => ({
            id: person.id,
            personNames: mapPersonNameDtosToReferencedPersonNameDtos(person.personNames),
            externalIds: person.externalIds,
          })
        )
      ),
      keyIds: await Promise.all(
        (_config.keyIds || []).map(
          (id: number): Promise<WorkDTO> =>
            workApi.getWork({ workId: id }).catch(() => ({ id, title: `Could not load work ${id}` }))
        )
      ),
      linking: _config.linking || [],
      useMainName: _config.useMainName || false,
      name: _config.name || '',
      style: _config.style || 'ieee-with-url',
    };
  };

  if (!config) return <Loading/>;

  const reset = (event: MouseEvent): void => {
    event.preventDefault();
    toast.confirm({
      message: intl.formatMessage({id: 'publist.confirm.reset.content'}),
      header: intl.formatMessage({id: 'publist.confirm.reset.header'}),
      icon: 'pi pi-info-circle',
      defaultFocus: 'reject',
      acceptClassName: 'p-button-danger',
      acceptLabel: intl.formatMessage({id: 'button.confirm'}),
      rejectLabel: intl.formatMessage({id: 'button.abort'}),
      accept: (): void => _reset(),
    });
  };

  const persist = async (event: MouseEvent): Promise<void> => {
    event.preventDefault();
    if(!apiKey) {
      return await create(event);
    }
    try{
      if (apiKey) {
        const pubListConfig: PubListConfig = parsePublistConfig(config);
        const response: ApiResponse<string> = await publistServiceApi.storeRaw({pubListConfig, apiKey});
        switch (response.raw.status) {
          case 200:
            toast.notify({
              severity: 'success',
              summary: intl.formatMessage({id: 'message.saving.success.title'}),
            });
            setBlockRequested(false);
            return;
          default:
            toast.error(null, 'message.saving.failed');
        }
      }
    } catch (err) {
      toast.error(err, 'message.saving.failed');
    }
  };

  const create = async (event: MouseEvent): Promise<void> => {
    try {
      event.preventDefault();
      const pubListConfig: PubListConfig = parsePublistConfig(config);
      const response: ApiResponse<string> = await publistServiceApi.storeNewRaw({pubListConfig});
      const newApiKey: string = await response.value();
      switch (response?.raw?.status) {
        case 200:
          toast.notify({
            severity: 'success',
            summary: intl.formatMessage({id: 'message.saving.success.title'}),
            content: intl.formatMessage({id: 'message.saving.success.message'}),
          });
          setBlockRequested(false);
          void navigate(`/publist/generator/${params.type}/${newApiKey}`, {replace: false});
          return;
        default:
          toast.error(null, 'message.saving.failed');
          break;
      }
    } catch (err) {
      toast.error(err, 'message.saving.failed');}
  };

  const settings: ReactNode = (
    <form className={'flex flex-column gap-2'}>
      <LabelledInputText
        id={'name'}
        value={config.name || ''}
        required={true}
        validation={{ valid: config.name && config.name.trim().length > 0 }}
        onChange={(name: string): void => {
          editConfig((state: PubListConfigInternal) => ({ ...state, name }));
        }}
        title={'publig.publist.name'}
      />
      <div className={'flex flex-column gap-1'}>
        <LabelledPersonSuggester
          id={'persons'}
          value={(config.personIds || []).map(referencedPersonToSelectedPerson)}
          required={
            (config.projectOrganisationConfig?.projectIds?.length || 0) +
              (config.projectOrganisationConfig?.organisationIds?.length || 0) ===
            0
          }
          validation={{
            valid:
              (config.personIds?.length || 0) +
                (config.projectOrganisationConfig?.projectIds?.length || 0) +
                (config.projectOrganisationConfig?.organisationIds?.length || 0) >
              0,
          }}
          onChange={(personIds: SelectedPerson[]): void => {
            editConfig((state: PubListConfigInternal) => ({
              ...state,
              personIds: personIds.map(selectedPersonToReferencedPerson),
            }));
          }}
          title={'publig.publist.persons'}
          multiple={true}
        />
        <TitledCheckbox
          title={'publig.publist.useMainName'}
          checked={config.useMainName || false}
          onChange={(event: CheckboxChangeEvent): void =>
            editConfig(
              (prevState: PubListConfigInternal): PubListConfigInternal => ({
                ...prevState,
                useMainName: event.target.checked,
              })
            )
          }
        />
      </div>
      <div className={'flex flex-column gap-1'}>
        <LabelledOrganisationSuggester
          id={'organisations'}
          value={(config.projectOrganisationConfig?.organisationIds || []).map(
            referencedOrganisationToSelectedOrganisation
          )}
          required={(config.personIds?.length || 0) + (config.projectOrganisationConfig?.projectIds?.length || 0) === 0}
          validation={{
            valid:
              (config.personIds?.length || 0) +
                (config.projectOrganisationConfig?.projectIds?.length || 0) +
                (config.projectOrganisationConfig?.organisationIds?.length || 0) >
              0,
          }}
          onChange={(organisations: SelectedOrganisation[]): void => {
            editConfig(
              (state: PubListConfigInternal): PubListConfigInternal => ({
                ...state,
                projectOrganisationConfig: {
                  ...state.projectOrganisationConfig,
                  organisationIds: organisations.map(selectedOrganisationToReferencedOrganisation).map(
                    (organisation: ReferencedOrganisationDTO): OrganisationDTO => ({
                      id: organisation.id,
                      name: organisation.name,
                    })
                  ),
                },
              })
            );
          }}
          title={'publig.publist.organisations'}
          multiple={true}
        />
        {config.projectOrganisationConfig.organisationIds.length > 0 && (
          <TitledCheckbox
            title={'publig.publist.includeOrganisationHierarchy'}
            checked={config.projectOrganisationConfig?.withOrganisationHierarchy}
            onChange={(event: CheckboxChangeEvent): void =>
              editConfig(
                (prevState: PubListConfigInternal): PubListConfigInternal => ({
                  ...prevState,
                  projectOrganisationConfig: {
                    ...prevState.projectOrganisationConfig,
                    withOrganisationHierarchy: event.target.checked,
                  },
                })
              )
            }
          />
        )}
      </div>
      <LabelledProjectSuggester
        id={'projects'}
        value={(config.projectOrganisationConfig?.projectIds || []).map(referencedProjectToSelectedProject)}
        required={
          (config.personIds?.length || 0) + (config.projectOrganisationConfig?.organisationIds?.length || 0) === 0
        }
        validation={{
          valid:
            (config.personIds?.length || 0) +
              (config.projectOrganisationConfig?.projectIds?.length || 0) +
              (config.projectOrganisationConfig?.organisationIds?.length || 0) >
            0,
        }}
        onChange={(projects: SelectedProject[]): void => {
          editConfig(
            (state: PubListConfigInternal): PubListConfigInternal => ({
              ...state,
              projectOrganisationConfig: {
                ...state.projectOrganisationConfig,
                projectIds: projects.map(
                  (project: SelectedProject): ProjectDTO => ({
                    id: project.id,
                    name: project.name,
                  })
                ),
              },
            })
          );
        }}
        title={'publig.publist.projects'}
        multiple={true}
      />
      <Accordion multiple={true}>
        {(config.projectOrganisationConfig?.projectIds.length > 0 ||
          config.projectOrganisationConfig?.organisationIds.length > 0) && (
          <AccordionTab header={intl.formatMessage({ id: 'publig.publist.tab.projectsOrganisations.title' })}>
            <div className={'flex flex-column gap-4'}>
              <TitledRadio
                inputId={'determinationType_members'}
                name={'determinationType'}
                value={'MEMBERS'}
                checked={config.projectOrganisationConfig?.determinationType === 'MEMBERS'}
                onChange={(event: RadioButtonChangeEvent): void => {
                  editConfig(
                    (prevState: PubListConfigInternal): PubListConfigInternal => ({
                      ...prevState,
                      projectOrganisationConfig: {
                        ...prevState.projectOrganisationConfig,
                        determinationType: event.value,
                      },
                    })
                  );
                }}
                title={'publig.publist.determinationType.members'}
              />
              {config.projectOrganisationConfig?.determinationType === 'MEMBERS' && (
                <div className={'flex flex-column gap-4 ml-4 pl-2 border-left-3 border-200'}>
                  <TitledCheckbox
                    title={'publig.publist.considerEmploymentPeriod'}
                    checked={config.projectOrganisationConfig.considerEmploymentPeriod || false}
                    onChange={(event: CheckboxChangeEvent): void =>
                      editConfig(
                        (prevState: PubListConfigInternal): PubListConfigInternal => ({
                          ...prevState,
                          projectOrganisationConfig: {
                            ...prevState.projectOrganisationConfig,
                            considerEmploymentPeriod: event.target.checked,
                          },
                        })
                      )
                    }
                  />
                  <div className={'flex flex-column gap-2'}>
                    <h4>
                      <Label label={intl.formatMessage({ id: 'publig.publist.filterMembers.label' })}></Label>
                    </h4>
                    {Object.values(PublistConfigProjectOrganisationFilterMembersEnum).map(
                      (value: string, index: number): ReactNode => (
                        <TitledRadio
                          key={index}
                          title={`publig.publist.filterMembers.${value.toLowerCase()}`}
                          checked={config.projectOrganisationConfig.filterMembers === value}
                          value={value}
                          onChange={(event: RadioButtonChangeEvent): void =>
                            editConfig(
                              (prevState: PubListConfigInternal): PubListConfigInternal => ({
                                ...prevState,
                                projectOrganisationConfig: {
                                  ...prevState.projectOrganisationConfig,
                                  filterMembers: event.value,
                                },
                              })
                            )
                          }
                        />
                      )
                    )}
                  </div>
                </div>
              )}
              <TitledRadio
                inputId={'determinationType_direct'}
                name={'determinationType'}
                value={'DIRECT'}
                checked={config.projectOrganisationConfig?.determinationType === 'DIRECT'}
                onChange={(event: RadioButtonChangeEvent): void =>
                  editConfig(
                    (prevState: PubListConfigInternal): PubListConfigInternal => ({
                      ...prevState,
                      projectOrganisationConfig: {
                        ...prevState.projectOrganisationConfig,
                        determinationType: event.value,
                      },
                    })
                  )
                }
                title={'publig.publist.determinationType.direct'}
              />
              <TitledRadio
                inputId={'determinationType_self'}
                name={'determinationType'}
                value={'SELF'}
                checked={config.projectOrganisationConfig?.determinationType === 'SELF'}
                onChange={(event: RadioButtonChangeEvent): void =>
                  editConfig(
                    (prevState: PubListConfigInternal): PubListConfigInternal => ({
                      ...prevState,
                      projectOrganisationConfig: {
                        ...prevState.projectOrganisationConfig,
                        determinationType: event.value,
                      },
                    })
                  )
                }
                title={'publig.publist.determinationType.self'}
              />
              {config.projectOrganisationConfig?.determinationType === 'SELF' && (
                <div className={'flex flex-column gap-4 ml-4 pl-2 border-left-3 border-200'}>
                  <LabelledMultiSelect
                    id={'restrictToRoles'}
                    value={config.projectOrganisationConfig.restrictToRoles}
                    onChange={(restrictToRoles: any): void =>
                      editConfig(
                        (prevState: PubListConfigInternal): PubListConfigInternal => ({
                          ...prevState,
                          projectOrganisationConfig: { ...prevState.projectOrganisationConfig, restrictToRoles },
                        })
                      )
                    }
                    title={'publig.publist.restrictToRoles'}
                    options={getVocabularyOptions(intl.locale, 'corporation_role_list')}
                  />
                  <LabelledMultiSelect
                    id={'excludeByRoles'}
                    value={config.projectOrganisationConfig.excludeByRoles}
                    onChange={(excludeByRoles: any): void =>
                      editConfig(
                        (prevState: PubListConfigInternal): PubListConfigInternal => ({
                          ...prevState,
                          projectOrganisationConfig: { ...prevState.projectOrganisationConfig, excludeByRoles },
                        })
                      )
                    }
                    title={'publig.publist.excludeByRoles'}
                    options={getVocabularyOptions(intl.locale, 'corporation_role_list')}
                  />
                </div>
              )}
            </div>
          </AccordionTab>
        )}
        <AccordionTab header={intl.formatMessage({ id: 'publig.publist.tab.citation.title' })}>
          <div className={'flex flex-column gap-4'}>
            <div className={'flex flex-column gap-2'}>
              <TitledAutoComplete
                id={'citation'}
                value={config.style}
                required={true}
                validation={{ valid: !!config.style }}
                onChange={(style: string): void =>
                  editConfig(
                    (prevState: PubListConfigInternal): PubListConfigInternal => ({
                      ...prevState,
                      style: style.endsWith('.csl') ? style.slice(0, -4) : style,
                    })
                  )
                }
                label={'publig.publist.style'}
                suggestions={Array.from(
                  getVocabulary('citation_styles_list', intl.locale),
                  ([value, label]): string => value
                )}
              />
              <div className={'flex justify-content-end'}>
                <ExternalLink className={'text-primary font-normal no-underline'} href="https://www.zotero.org/styles">
                  {intl.formatMessage({ id: 'publig.publist.style.additional' })}
                </ExternalLink>
              </div>
            </div>
            <LabelledDropdown
              id={'publig.publist.locale'}
              value={config.locale}
              required={true}
              validation={{ valid: !!config.locale }}
              onChange={(locale: string): void =>
                editConfig(
                  (prevState: PubListConfigInternal): PubListConfigInternal => ({
                    ...prevState,
                    locale,
                  })
                )
              }
              title={'publig.publist.language'}
              options={getVocabularyOptions(intl.locale, 'csl_locale_list')}
            />
          </div>
        </AccordionTab>
        <AccordionTab header={intl.formatMessage({ id: 'publig.publist.tab.groupContent.title' })}>
          <div className={'flex flex-column gap-4'}>
            <TitledCheckbox
              inputId={'publig.publist.groupByYear'}
              title={'publig.publist.groupByYear'}
              checked={config.groupByYear}
              onChange={(e: CheckboxChangeEvent): void =>
                editConfig(
                  (prevState: PubListConfigInternal): PubListConfigInternal => ({
                    ...prevState,
                    groupByYear: e.checked,
                  })
                )
              }
            />
            <TitledCheckbox
              inputId={'publig.publist.groupByType'}
              title={'publig.publist.groupByType'}
              checked={config.groupByType}
              onChange={(e: CheckboxChangeEvent): void =>
                editConfig(
                  (prevState: PubListConfigInternal): PubListConfigInternal => ({
                    ...prevState,
                    groupByType: e.checked,
                  })
                )
              }
            />
            <TitledCheckbox
              inputId={'publig.publist.toc'}
              title={'publig.publist.toc'}
              disabled={!config.groupByType && !config.groupByYear}
              checked={config.toc}
              onChange={(e: CheckboxChangeEvent): void =>
                editConfig(
                  (prevState: PubListConfigInternal): PubListConfigInternal => ({
                    ...prevState,
                    toc: e.checked,
                  })
                )
              }
            />
            <LabelledDropdown
              id={'publig.publist.pubsort'}
              value={config.pubsort}
              onChange={(pubsort: any): void =>
                editConfig(
                  (prevState: PubListConfigInternal): PubListConfigInternal => ({
                    ...prevState,
                    pubsort,
                  })
                )
              }
              required={true}
              title={'publig.publist.pubsort'}
              options={publistSortOptions.map(
                (item: SelectItem): SelectItem => ({
                  value: item.value,
                  label: intl.formatMessage({ id: item.label }),
                })
              )}
            />
          </div>
        </AccordionTab>
        <AccordionTab header={intl.formatMessage({ id: 'publig.publist.tab.personFilters.title' })}>
          <div className={'flex flex-column gap-4'}>
            <LabelledMultiSelect
              id={'publig.publist.filterByRole'}
              value={config.filterByRole}
              onChange={(filterByRole: any): void =>
                editConfig(
                  (prevState: PubListConfigInternal): PubListConfigInternal => ({
                    ...prevState,
                    filterByRole,
                  })
                )
              }
              title={'publig.publist.filterByRole'}
              options={getVocabularyOptions(intl.locale, 'person_role_list')}
            />
            <LabelledMultiSelect
              id={'publig.publist.filterByRoleExcl'}
              value={config.filterByRoleExcl}
              onChange={(filterByRoleExcl: any): void =>
                editConfig(
                  (prevState: PubListConfigInternal): PubListConfigInternal => ({
                    ...prevState,
                    filterByRoleExcl,
                  })
                )
              }
              title={'publig.publist.filterByRoleExcl'}
              options={getVocabularyOptions(intl.locale, 'person_role_list')}
            />
            <LabelledMultiSelect
              id={'publig.publist.filterPersonByRole'}
              value={config.filterPersonByRole}
              onChange={(filterPersonByRole: any): void =>
                editConfig(
                  (prevState: PubListConfigInternal): PubListConfigInternal => ({
                    ...prevState,
                    filterPersonByRole,
                  })
                )
              }
              title={'publig.publist.filterPersonByRole'}
              options={getVocabularyOptions(intl.locale, 'person_role_list')}
            />
            <LabelledPersonSuggester
              id={'publig.publist.filterPersons'}
              value={config.filterPersons.map(referencedPersonToSelectedPerson)}
              onChange={(filterPersons: SelectedPerson[]): void =>
                editConfig(
                  (prevState: PubListConfigInternal): PubListConfigInternal => ({
                    ...prevState,
                    filterPersons: filterPersons.map(selectedPersonToReferencedPerson),
                  })
                )
              }
              title={'publig.publist.filterPersons'}
              multiple={true}
            />
          </div>
        </AccordionTab>
        <AccordionTab header={intl.formatMessage({ id: 'publig.publist.tab.generalFilters.title' })}>
          <div className={'flex flex-column gap-4'}>
            <LabelledMultiSelect
              id={'publig.publist.filterByType'}
              value={config.filterByType}
              onChange={(filterByType: any): void =>
                editConfig(
                  (prevState: PubListConfigInternal): PubListConfigInternal => ({
                    ...prevState,
                    filterByType,
                  })
                )
              }
              title={'publig.publist.filterByType'}
              options={getVocabularyOptions(intl.locale, 'pubtype_list', 'all')}
            />
            <LabelledMultiSelect
              id={'publig.publist.excludeByType'}
              value={config.excludeByType}
              onChange={(excludeByType: any): void =>
                editConfig(
                  (prevState: PubListConfigInternal): PubListConfigInternal => ({
                    ...prevState,
                    excludeByType,
                  })
                )
              }
              title={'publig.publist.excludeByType'}
              options={getVocabularyOptions(intl.locale, 'pubtype_list', 'all')}
            />
            <FilterByYear
              label={'publig.publist.filterByYear'}
              yearPeriods={config.filterByYear?.length < 1 ? [{ start: null, end: null }] : config.filterByYear}
              onChange={(filterByYear: YearPeriod[]): void =>
                editConfig(
                  (prevState: PubListConfigInternal): PubListConfigInternal => ({
                    ...prevState,
                    filterByYear,
                  })
                )
              }
            />
            <FilterByCreated
              label={'publig.publist.filterByCreated'}
              datePeriods={
                config.filterByCreationDate?.length < 1
                  ? [
                      {
                        start: null,
                        end: null,
                      },
                    ]
                  : config.filterByCreationDate
              }
              onChange={(filterByCreationDate: DatePeriod[]): void =>
                editConfig(
                  (prevState: PubListConfigInternal): PubListConfigInternal => ({
                    ...prevState,
                    filterByCreationDate,
                  })
                )
              }
            />
            <LabelledMultiSelect
              id={'publist.filterByLang'}
              value={config.filterByLang}
              onChange={(filterByLang: any): void =>
                editConfig(
                  (prevState: PubListConfigInternal): PubListConfigInternal => ({
                    ...prevState,
                    filterByLang,
                  })
                )
              }
              title={'publig.publist.filterByLang'}
              options={getVocabularyOptions(intl.locale, 'language_list', 'publig')}
            />
            <TitledYesNo
              value={config.filterByPr}
              title={'publig.publist.filterByPr'}
              onChange={(filterByPr: boolean): void =>
                editConfig(
                  (prevState: PubListConfigInternal): PubListConfigInternal => ({
                    ...prevState,
                    filterByPr,
                  })
                )
              }
              inputId={'publist.peerReviewed'}
            />
          </div>
        </AccordionTab>
        <AccordionTab header={intl.formatMessage({ id: 'publig.publist.tab.highlights.title' })}>
          <div className={'flex flex-column gap-4'}>
            <LabelledWorkSuggester
              id={'publist.keyIds'}
              value={config.keyIds}
              onChange={(keyIds: ReferencedWorkDTO[]): void =>
                editConfig(
                  (prevState: PubListConfigInternal): PubListConfigInternal => ({
                    ...prevState,
                    keyIds,
                  })
                )
              }
              title={'publig.publist.keyIds'}
              multiple={true}
            />
            <LabelledWorkSuggester
              id={'publist.filterIds'}
              value={config.filterIds}
              onChange={(filterIds: ReferencedWorkDTO[]): void =>
                editConfig(
                  (prevState: PubListConfigInternal): PubListConfigInternal => ({
                    ...prevState,
                    filterIds,
                  })
                )
              }
              title={'publig.publist.filterIds'}
              multiple={true}
            />
          </div>
        </AccordionTab>
        <AccordionTab header={intl.formatMessage({ id: 'publig.publist.tab.textlinks.title' })}>
          <div className={'flex flex-column gap-4'}>
            <h4>
              <Label label={intl.formatMessage({ id: 'publig.publist.replaceText.label' })} />
            </h4>
            <MultiInputControls<PubListConfigReplacements>
              canEdit={true}
              lines={config.replacements || []}
              addNew={(rows: PubListConfigReplacements[]): number => rows.push(getDefaultPubListConfigReplacements())}
              onChange={(replacements: PubListConfigReplacements[]): void =>
                editConfig(
                  (prevState: PubListConfigInternal): PubListConfigInternal => ({
                    ...prevState,
                    replacements,
                  })
                )
              }
              renderLines={(
                replacement: PubListConfigReplacements,
                onLineChange: (newValues: PubListConfigReplacements) => void,
                actions1: Actions,
                idx: number
              ): ReactElement => (
                <InputGroup delete={{ action: actions1.delete, disabled: false }}>
                  <LabelledInputText
                    id={`replacement-${idx}-key`}
                    value={replacement.key}
                    validation={{ valid: replacement.key && replacement.key.length > 0 }}
                    required={true}
                    onChange={(key: any): void => onLineChange({ ...replacement, key })}
                    label={'publig.publist.replaceText.key'}
                  />
                  <LabelledInputText
                    id={`replacement-${idx}-value`}
                    value={replacement.value}
                    onChange={(value: any): void => onLineChange({ ...replacement, value })}
                    label={'publig.publist.replaceText.value'}
                    required={true}
                  />
                </InputGroup>
              )}
            />
            <>{config.linking}</>
            <LabelledMultiSelect
              id={'publig.publist.linking'}
              value={config.linking}
              onChange={(linking: string[]): void =>
                editConfig(
                  (prevState: PubListConfigInternal): PubListConfigInternal => ({
                    ...prevState,
                    linking,
                  })
                )
              }
              display={'chip'}
              title={'publig.publist.linking'}
              options={getVocabularyOptions(intl.locale, 'url_list_publig')}
            />
          </div>
        </AccordionTab>
      </Accordion>
      <Button
        label={intl.formatMessage({ id: 'publig.button.preview' })}
        disabled={!checkValidation()}
        onClick={loadPreview}
      ></Button>
      {authorizer.isNotAnonymous() && (
        <Button
          label={intl.formatMessage({ id: 'publig.button.save' })}
          disabled={!checkValidation()}
          onClick={persist}
        ></Button>
      )}
      {authorizer.isNotAnonymous() && !!apiKey && (
        <Button
          label={intl.formatMessage({ id: 'publig.button.saveAs' })}
          disabled={!checkValidation()}
          onClick={create}
        ></Button>
      )}
    </form>
  );

  return <Layout
    title={intl.formatMessage({id: `publig.header.${params.type}.${apiKey ? 'edit' : 'create'}`})}
    headerImage={require('../../Settings/img/banners/publig.svg')}
    sidebarContent={settings}
    sidebarVisible={sidebarVisible}
    setSidebarVisible={setSidebarVisible}
    sidebarTitle={
      <div className={'flex flex-row gap-3'}>
        <div className={'flex-grow-1'}>
          <FormattedMessage id="publig.sidebar.title"/>
        </div>
        <div className={'flex-none'}>
          <Button title={intl.formatMessage({id: 'publig.button.reset'})}
                  icon={'pi pi-replay'}
                  outlined={true}
                  onClick={reset}></Button>
        </div>
      </div>
    }
  >
    <div className={'text-overflow-clip px-3'}>
      {loadingPreview && <Loading/>}
      {!loadingPreview && error &&
        <ErrorComponent code={error}/>
      }
      {!loadingPreview && !error && preview &&
        <div>
          <div className={'flex flex-row gap-4 justify-content-between items-center'}>
            <h1>{config.name && config.name.length > 0 ? config.name : intl.formatMessage({id: 'publist.unnamedPublist'})}</h1>
            <div>
              <Menu model={[
                {label: 'txt', command: (): void => download('txt')},
                {label: 'html', command: (): void => download('html')},
              ]} popup ref={menuDownload} id="popup_menu_download" popupAlignment="right"/>
              <Button icon="pi pi-download" className="mr-2"
                      onClick={(event): void => menuDownload.current.toggle(event)}
                      aria-controls="popup_menu_download" aria-haspopup/>

            </div>
          </div>
          <div>
            <Fieldset title={'publig.preview'}>
              <div className={'preview'} dangerouslySetInnerHTML={{__html: preview}}></div>
            </Fieldset>
          </div>
        </div>
      }
    </div>
  </Layout>;
};

export default PublicationListEditor;
